/**
 * Tiny isomorphic helper to ship client-side render errors to the
 * /api/route-health/render-error endpoint. Three callers:
 *   1. RenderErrorBoundary (componentDidCatch)
 *   2. window.addEventListener('error', ...)
 *   3. window.addEventListener('unhandledrejection', ...)
 *
 * Throttled per-message-hash so a tight error loop can't DDOS the endpoint.
 * Uses navigator.sendBeacon when available (works during page unload) and
 * falls back to fetch().
 *
 * Extension-noise filter: browser extensions (MetaMask, Google Translate,
 * various iframe-sandbox anti-fingerprinting tools, etc.) inject scripts
 * into every tab and routinely throw exceptions that `window.onerror`
 * captures even though they have nothing to do with our app. We drop
 * those silently so the Route-Health render-errors panel stays signal-rich.
 */
import { getBootMeta } from '@/lib/buildMeta';

const THROTTLE_MS = 60 * 1000; // 1 min — same hash twice within 60s = drop
const _lastSeen = new Map();   // hash → timestamp

// Case-insensitive substring patterns that indicate a browser-extension /
// 3rd-party-injection error (NOT an app render error). Keep this list
// tight — false-positives here mean we'd miss real crashes.
const EXTENSION_NOISE_PATTERNS = [
  'metamask',                // MetaMask wallet — "Failed to connect to MetaMask"
  'ethereum',                // window.ethereum injection from wallets
  'bufferbelongstoiframe',   // Google Translate / iframe-sandbox extensions
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'extension context invalidated',
  'solana',                  // Phantom / Solflare wallets
  'tronlink',                // TronLink wallet
  'web3',                    // generic wallet injection
  'resizeobserver loop',     // Chrome non-fatal warning
  'script error.',           // cross-origin generic (no real detail to act on)
  'non-error promise rejection captured',
  'googletag',               // Google Ad Manager
  'gtag is not defined',     // missing analytics tag — harmless
];

function _isExtensionNoise(message, stack) {
  const hay = `${message || ''}\n${stack || ''}`.toLowerCase();
  return EXTENSION_NOISE_PATTERNS.some((p) => hay.includes(p));
}

function _hash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h.toString(36);
}

export function sendRenderErrorBeacon({ message, stack = '', path = '' }) {
  if (!message) return;
  // Drop browser-extension / 3rd-party injection noise silently.
  if (_isExtensionNoise(message, stack)) return;
  const key = _hash((message || '') + '|' + (path || ''));
  const now = Date.now();
  if (_lastSeen.has(key) && now - _lastSeen.get(key) < THROTTLE_MS) return;
  _lastSeen.set(key, now);

  const meta = getBootMeta() || {};
  const payload = JSON.stringify({
    path: path || (typeof window !== 'undefined' ? window.location.pathname : ''),
    message: String(message).slice(0, 600),
    stack: String(stack || '').slice(0, 4000),
    build_id: meta.build_id || 'unknown',
  });
  const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/route-health/render-error`;

  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch (_) {}
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
}

let _installed = false;

/** Wire window.onerror + unhandledrejection ONCE at app boot. */
export function installGlobalRenderErrorBeacon() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;
  window.addEventListener('error', (e) => {
    try {
      sendRenderErrorBeacon({
        message: e?.message || 'window.onerror',
        stack: e?.error?.stack || '',
        path: window.location.pathname,
      });
    } catch (_) {}
  });
  window.addEventListener('unhandledrejection', (e) => {
    try {
      const reason = e?.reason;
      sendRenderErrorBeacon({
        message: (reason?.message || String(reason || 'unhandledrejection')).slice(0, 600),
        stack: reason?.stack || '',
        path: window.location.pathname,
      });
    } catch (_) {}
  });
}
